import React, { useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Popconfirm,
  Radio,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { Dayjs } from 'dayjs';
import { useDeleteData, useUpdateData } from '../../../hooks';
import { convertDate, dateFormat } from '../../../lib/utils';
import { ProderPlan, ShipDecisionRequest } from '../../../types';

const { Text } = Typography;

const fmt = (d: string | null) => (d ? convertDate(d, dateFormat) : '—');

/**
 * Spec step 12 / flow step 5: is Marketing's requested date workable? The
 * verdict comes from the plan; the planner records the decision and
 * Marketing's answer (Edward: the planner records it, no routing).
 */
const ShipDecisionBlock: React.FC<{ api: string; plan: ProderPlan }> = ({
  api,
  plan,
}) => {
  const sd = plan.shipDecision;
  const cur = sd.current;
  const act = useUpdateData<ShipDecisionRequest>(api);
  const remove = useDeleteData(api);
  const [proposed, setProposed] = useState<Dayjs | null>(null);
  const [answer, setAnswer] = useState<'APPROVED' | 'DISCUSS'>('APPROVED');
  const [answeredBy, setAnsweredBy] = useState('');
  const [agreed, setAgreed] = useState<Dayjs | null>(null);
  const [note, setNote] = useState('');

  const send = (form: ShipDecisionRequest, done: string) =>
    act.mutate(
      { id: 'ship-decision', form },
      {
        onSuccess: () => {
          message.success(done);
          setProposed(null);
          setAnsweredBy('');
          setAgreed(null);
          setNote('');
        },
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Save failed'),
      },
    );

  const pending = cur?.marketingAnswer === 'PENDING';

  return (
    <div className={'space-y-2'}>
      <Space wrap align={'center'}>
        <Text strong>Ship date</Text>
        <Text type={'secondary'}>
          requested {fmt(sd.requestedDate)} · plan ready {fmt(sd.readyDate)}
          {sd.shipGapNotSetUp ? ' (ship gap not set up)' : ''}
        </Text>
        {sd.verdict === 'WORKABLE' && <Tag color={'green'}>workable</Tag>}
        {sd.verdict === 'NOT_WORKABLE' && (
          <Tag color={'red'}>not workable · {sd.daysLate} day(s) late</Tag>
        )}
        {sd.verdict === 'NOT_PLANNED' && <Tag>not planned yet</Tag>}
        {sd.verdict === 'NO_REQUEST' && <Tag>no requested date on the BOM</Tag>}
        {cur?.confirmedDate && (
          <Tag color={'blue'}>confirmed {fmt(cur.confirmedDate)}</Tag>
        )}
        {pending && (
          <Tag color={'gold'}>
            proposed {fmt(cur?.proposedDate ?? null)} · waiting for Marketing
          </Tag>
        )}
      </Space>

      {sd.drift && (
        <Alert
          type={'error'}
          showIcon
          message={`The plan is now ready ${fmt(
            sd.readyDate,
          )}, after the confirmed ship date ${fmt(
            cur?.confirmedDate ?? null,
          )} — re-plan or agree a new date`}
        />
      )}

      {cur && (
        <Text type={'secondary'} className={'text-xs'}>
          last step: {cur.action}
          {cur.marketingAnswer && cur.marketingAnswer !== 'PENDING'
            ? ` · ${cur.marketingAnswer} by ${cur.answeredBy ?? '—'} on ${fmt(
                cur.answeredOn,
              )}`
            : ''}
          {' · '}recorded by {cur.recordedBy ?? '—'} {cur.recordedAt ?? ''}
          {cur.note ? ` · ${cur.note}` : ''}
        </Text>
      )}

      <Space wrap>
        {sd.verdict === 'WORKABLE' && !pending && (
          <Button
            size={'small'}
            loading={act.isPending}
            onClick={() =>
              send({ action: 'CONFIRM_REQUESTED' }, 'Requested date confirmed')
            }
          >
            Confirm requested date
          </Button>
        )}
        {sd.readyDate && !pending && (
          <>
            <DatePicker
              size={'small'}
              value={proposed}
              placeholder={`propose (default ${fmt(sd.readyDate)})`}
              onChange={setProposed}
            />
            <Button
              size={'small'}
              loading={act.isPending}
              onClick={() =>
                send(
                  {
                    action: 'PROPOSE',
                    proposedDate: proposed ? proposed.format(dateFormat) : null,
                  },
                  'Workable date proposed',
                )
              }
            >
              Propose workable date
            </Button>
          </>
        )}
        {cur && (
          <Popconfirm title={'Remove the ship-date decision?'} onConfirm={() =>
            remove.mutate(
              { id: 'ship-decision' },
              {
                onSuccess: () => message.success('Decision removed'),
                onError: (e) =>
                  message.error(e?.response?.data?.message ?? 'Remove failed'),
              },
            )
          }>
            <Button size={'small'} danger>
              Remove decision
            </Button>
          </Popconfirm>
        )}
      </Space>

      {pending && (
        <Space wrap align={'center'}>
          <Text>Marketing&apos;s answer:</Text>
          <Radio.Group
            size={'small'}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          >
            <Radio.Button value={'APPROVED'}>Approved</Radio.Button>
            <Radio.Button value={'DISCUSS'}>Discussed with Prod Manager</Radio.Button>
          </Radio.Group>
          <Input
            size={'small'}
            style={{ width: 180 }}
            placeholder={'who answered'}
            value={answeredBy}
            onChange={(e) => setAnsweredBy(e.target.value)}
          />
          {answer === 'DISCUSS' && (
            <DatePicker
              size={'small'}
              value={agreed}
              placeholder={'date agreed'}
              onChange={setAgreed}
            />
          )}
          <Input
            size={'small'}
            style={{ width: 200 }}
            placeholder={'note (optional)'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size={'small'}
            type={'primary'}
            loading={act.isPending}
            onClick={() =>
              send(
                {
                  action: 'ANSWER',
                  answer,
                  answeredBy: answeredBy.trim() || null,
                  confirmedDate: agreed ? agreed.format(dateFormat) : null,
                  note: note.trim() || null,
                },
                'Answer recorded',
              )
            }
          >
            Record answer
          </Button>
        </Space>
      )}
    </div>
  );
};

export default ShipDecisionBlock;
